import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Facebook, ArrowUpRight } from "lucide-react";
import { ORG, NAV_LINKS } from "../data/site";
import { PlaceholderTag } from "./common";

const navClass = (light) => ({ isActive }) =>
  `relative text-sm font-medium tracking-wide transition-colors duration-300 ${
    light
      ? isActive
        ? "text-white after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-full after:bg-terracotta"
        : "text-white/70 hover:text-white"
      : isActive
        ? "text-charcoal after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-full after:bg-terracotta"
        : "text-charcoal/60 hover:text-charcoal"
  }`;

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const light = pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-warmwhite/85 border-b border-charcoal/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-[90rem] mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-6">
          <Link to="/" data-testid="header-logo" className="flex flex-col leading-none" aria-label="Susanna Charitable Trust — home">
            <span className={`font-serif text-2xl font-medium tracking-tight transition-colors duration-500 ${light ? "text-white" : "text-charcoal"}`}>Susanna</span>
            <span className={`text-[10px] font-mono uppercase tracking-[0.25em] mt-1 transition-colors duration-500 ${light ? "text-white/60" : "text-charcoal/50"}`}>
              Charitable Trust
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navClass(light)} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link
              to="/donate"
              data-testid="header-donate-button"
              className="rounded-full bg-terracotta text-white px-6 py-2.5 text-sm font-semibold tracking-wide hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-300"
            >
              Donate
            </Link>
            <button
              data-testid="mobile-menu-button"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              className={`lg:hidden p-2 -mr-2 transition-colors duration-500 ${light || open ? "text-white" : "text-charcoal"}`}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-charcoal text-warmwhite flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block py-3 font-serif text-4xl font-light tracking-tight border-b border-white/10"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-10 text-xs font-mono uppercase tracking-[0.25em] text-white/50"
            >
              Reg. No. {ORG.registration} · {ORG.location}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-charcoal text-warmwhite rounded-t-3xl mt-24">
      <div className="max-w-[90rem] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5">
            <p className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-tight">
              Susanna<br />Charitable Trust
            </p>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-white/60">
              Reg. No. {ORG.registration}
            </p>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">
              Founded on {ORG.founded} in Ahmedabad, Gujarat — helping underprivileged children with their
              education and supporting women's empowerment.
            </p>
            <Link
              to="/donate"
              data-testid="footer-donate-button"
              className="mt-8 inline-flex rounded-full bg-terracotta text-white px-8 py-3.5 text-sm font-semibold hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-300"
            >
              Donate
            </Link>
          </div>
          <div className="md:col-span-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Explore</p>
            <ul className="mt-6 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/75 hover:text-white transition-colors" data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Contact</p>
            <ul className="mt-6 space-y-4 text-sm text-white/75">
              <li className="flex flex-col gap-2">
                <span>{ORG.address || "Address"}</span>
                {!ORG.address && <PlaceholderTag dark />}
              </li>
              <li className="flex flex-col gap-2">
                <span>{ORG.phone || "Phone"}</span>
                {!ORG.phone && <PlaceholderTag dark />}
              </li>
              <li className="flex flex-col gap-2">
                <span>{ORG.email || "Email"}</span>
                {!ORG.email && <PlaceholderTag dark />}
              </li>
              <li>
                <a
                  href={ORG.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-facebook-link"
                  className="inline-flex items-center gap-2 text-white/75 hover:text-white transition-colors"
                  aria-label="Susanna Charitable Trust on Facebook"
                >
                  <Facebook className="h-4 w-4" /> Facebook <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-white/50">© {year} {ORG.name}. All rights reserved.</p>
          <div className="flex gap-6 text-xs text-white/50">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">Terms / Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen bg-warmwhite">
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
